import { Autocomplete } from "@mui/material";

import { Box } from "ui/box/box";
import { Tooltip } from "ui/tooltip/tooltip";
import { FieldLabel } from "ui/forms/field-label/field-label";
import { FieldErrorMessage, FieldHelperText } from "ui/forms/field-message/field-message";
import { mergeSx } from "ui/merge-sx";

import { SelectChips } from "ui/select/components/select-chips";
import { SelectInput } from "ui/select/components/select-input";
import { SelectOption } from "ui/select/components/select-option";
import { getOptionLabelString, isSameOption } from "ui/select/select.helpers";
import {
  CONTROL_FRAME_SX,
  FIELD_ROW_SX,
  FIELD_SX,
  getControlSx,
  VALUE_TOOLTIP_WRAPPER_SX,
} from "ui/select/select.styles";
import { useSelect } from "ui/select/use-select";

import Icons from "assets";

import type { AutocompleteRenderValue, AutocompleteRenderValueGetItemProps } from "@mui/material";
import type { OptionType, SelectProps } from "ui/select/select.types";

import { CLEAR_ICON_SIZE } from "theme/components/field-geometry";

export const Select = <Option extends OptionType = OptionType>({
  label,
  selectName,
  ariaLabel,
  size = "medium",
  options,
  value,
  defaultValue,
  onChange,
  onBlur,
  name,
  placeholder = "",
  multiple = false,
  disabled = false,
  required = false,
  infoTooltip,
  disableClearable,
  errMessage,
  helperText,
  actionTrigger,
  startIcon,
  endIcon,
  formatOptionLabel,
  renderSelectedValueAction,
  filterOption,
  autoFocus,
  onInputChange,
  isSearchable = true,
  onOpen,
  onClose,
  getOptionDisabled,
  sx,
  controlSx,
  ref,
  ...autocompleteProps
}: SelectProps<Option> & { ref?: React.Ref<HTMLInputElement> }) => {
  const {
    autocompleteProps: passedThrough,
    inputAriaProps,
    inputName,
    ariaDescribedBy,
    hasError,
    showHelperText,
    hasStartIcon,
    forcePopupIcon,
    isMenuOpen,
    isValueTooltipSuppressed,
    selectedOption,
    selectedLabel,
    selectedValueAction,
    valueProps,
    handleChange,
    handleInputChange,
    handleMenuOpen,
    handleMenuClose,
    setInputRef,
    isOptionDisabled,
    filterOptions,
  } = useSelect<Option, typeof autocompleteProps>({
    passThroughProps: autocompleteProps,
    selectName,
    name,
    errMessage,
    helperText,
    disabled,
    startIcon,
    value,
    defaultValue,
    onChange,
    onInputChange,
    onOpen,
    onClose,
    getOptionDisabled,
    multiple,
    filterOption,
    renderSelectedValueAction,
    ref,
  });

  const renderChips = (
    selected: AutocompleteRenderValue<Option, boolean, false>,
    getItemProps: AutocompleteRenderValueGetItemProps<boolean>,
  ) => (
    <SelectChips
      disabled={disabled}
      getItemProps={getItemProps}
      isMenuOpen={isMenuOpen}
      selected={selected}
      size={size}
    />
  );

  return (
    <Box sx={mergeSx(FIELD_SX[size], sx)}>
      {!!label && (
        <FieldLabel
          name={selectName}
          required={required}
          disabled={disabled}
          infoTooltip={infoTooltip}
          size={size}>
          {label}
        </FieldLabel>
      )}
      <Box sx={FIELD_ROW_SX}>
        <Tooltip
          label={selectedLabel}
          placement="bottom-end"
          width={600}
          suppressed={isValueTooltipSuppressed}
          wrapperSx={VALUE_TOOLTIP_WRAPPER_SX}>
          <Box sx={CONTROL_FRAME_SX}>
            <Autocomplete<Option, boolean, boolean, false>
              // First, so nothing it carries can override what the adapter sets below. The type
              // already omits every prop the adapter owns.
              {...passedThrough}
              // Without this MUI generates the input's id, leaving `FieldLabel`'s `htmlFor` pointing
              // at nothing: clicking the label neither focuses nor opens the control.
              id={selectName}
              multiple={multiple}
              options={options}
              disabled={disabled}
              disableCloseOnSelect={multiple}
              disableClearable={disableClearable}
              clearIcon={
                <Icons.QuickTipClose width={CLEAR_ICON_SIZE[size]} height={CLEAR_ICON_SIZE[size]} />
              }
              forcePopupIcon={forcePopupIcon}
              getOptionLabel={getOptionLabelString}
              filterOptions={filterOptions}
              isOptionEqualToValue={isSameOption}
              getOptionDisabled={isOptionDisabled}
              onOpen={handleMenuOpen}
              onClose={handleMenuClose}
              onChange={handleChange}
              onInputChange={handleInputChange}
              sx={mergeSx(getControlSx(size, hasStartIcon, multiple), controlSx)}
              {...valueProps}
              // `renderValue` is v9's replacement for `renderTags`, and it doubles as the switch telling
              // `useAutocomplete` the value lives in an adornment rather than the input: passed at all,
              // `getInputValue` returns `''` (`useAutocomplete.js:71`) and a single select's display
              // text goes blank. So it is spread in only when multiple.
              {...(multiple ? { renderValue: renderChips } : {})}
              renderOption={(optionProps, option, { selected }) => (
                <SelectOption
                  formatOptionLabel={formatOptionLabel}
                  multiple={multiple}
                  option={option}
                  optionProps={optionProps}
                  selected={selected}
                />
              )}
              renderInput={(params) => (
                <SelectInput
                  ariaProps={inputAriaProps}
                  ariaDescribedBy={ariaDescribedBy}
                  ariaLabel={ariaLabel}
                  autoFocus={autoFocus}
                  startIcon={startIcon}
                  endIcon={endIcon}
                  hasError={hasError}
                  isSearchable={isSearchable}
                  inputRef={setInputRef}
                  name={inputName}
                  onBlur={onBlur}
                  params={params}
                  placeholder={placeholder}
                  selectedOption={selectedOption}
                  selectedValueAction={selectedValueAction}
                  size={size}
                />
              )}
            />
          </Box>
        </Tooltip>
        {actionTrigger}
      </Box>
      {showHelperText && <FieldHelperText name={selectName} message={helperText} />}
      {hasError && <FieldErrorMessage name={selectName} message={errMessage} />}
    </Box>
  );
};
